import * as RadioPrimitive from "@radix-ui/react-radio-group";
import {
  radioGroup,
  radioIndicator,
  radioItem,
  radioWrapper
} from "./RadioGroup.css";

/**
 * Props for the RadioItem component.
 */
export interface RadioItemProps {
  /**
   * The label displayed next to the Radio.
   */
  label?: string;
  /**
   * The value used to identify which option is selected.
   */
  value: string;
  /**
   * Whether the Radio is disabled.
   */
  disabled?: boolean;
}

/**
 * Props for the RadioGroup component.
 */
export interface RadioGroupProps {
  /**
   * The name of the group. Submitted with its owning form as part of a name/value pair.
   */
  name: string;
  /**
   * The options for the radio group.
   */
  items: RadioItemProps[];
  /**
   * Callback function called when the value changes.
   */
  onChange: (value: string) => void;
  /**
   * The controlled value of the radio item to check.
   */
  value?: string;
  /**
   * The orientation of the component.
   */
  orientation?: "horizontal" | "vertical";
  /**
   * Whether the RadioGroup is disabled.
   */
  disabled?: boolean;
  /**
   * Whether the RadioGroup is required.
   */
  required?: boolean;
}

export function RadioGroup({
  name,
  items,
  value,
  disabled = false,
  required = false,
  orientation = "horizontal",
  onChange
}: RadioGroupProps) {
  return (
    <RadioPrimitive.Root
      className={radioGroup}
      name={name}
      value={value}
      disabled={disabled}
      required={required}
      orientation={orientation}
      onValueChange={onChange}
    >
      {items.map((item: RadioItemProps) => {
        const { label, value, disabled } = item;

        return (
          <label
            key={`radio-${name}-${value}`}
            className={radioWrapper}
            data-disabled={disabled}
          >
            <RadioPrimitive.Item
              className={radioItem}
              value={value}
              disabled={disabled}
            >
              <RadioPrimitive.Indicator className={radioIndicator} />
            </RadioPrimitive.Item>

            {label}
          </label>
        );
      })}
    </RadioPrimitive.Root>
  );
}
